import compact from 'lodash/compact'
import forEach from 'lodash/forEach'
import groupBy from 'lodash/groupBy'
import includes from 'lodash/includes'
import isEqual from 'lodash/isEqual'
import join from 'lodash/join'
import map from 'lodash/map'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import omitBy from 'lodash/omitBy'
import reduce from 'lodash/reduce'
import replace from 'lodash/replace'
import size from 'lodash/size'
import snakeCase from 'lodash/snakeCase'
import sortBy from 'lodash/sortBy'
import split from 'lodash/split'
import startsWith from 'lodash/startsWith'
import tail from 'lodash/tail'
import without from 'lodash/without'
import { useRouter } from 'next/router'
import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { ampli } from '@Analytics/ampli'
import { BlockTypeWithGlobal, ProductListBlockFacetType, ProductListBlockFragmentFragment } from '@Generated/graphql'
import { algoliaFetcher } from '@Globals/Algolia/Fetcher'
import {
  AlgoliaProductTile,
  BlockConfigAlgolia,
  FacetChip,
  FacetDynamicConfig,
  FacetDynamicConfigValue,
  FacetStats,
  NumericFilter,
  Options,
  PageProductListBlockGuidedFilter,
  ProductSorting,
} from '@Globals/Algolia/Types'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { gtagEvent } from '@Globals/Google/GTag'
import { MetadataContext } from '@Globals/Metadata'
import { getCurrencyUnit } from '@Globals/PriceHelpers'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

export interface ProductSearchContextType {
  currentHits: number
  facetChips: FacetChip[]
  facetConfig: ProductListBlockFragmentFragment['facetConfig']
  facetDynamicConfig: Record<string, FacetDynamicConfig>
  guidedFilters: PageProductListBlockGuidedFilter[] | null
  hasActiveFilter: boolean
  hits: Record<string, AlgoliaProductTile>
  initialFacetStats: FacetStats
  numericFilters: Record<string, NumericFilter>
  objectIDs: string[]
  resetFilters: () => void
  setRangeFilter: (key: string, min: number | undefined, max: number | undefined) => void
  sort: (sortKey: ProductSorting) => void
  sorting: ProductSorting
  toggleFilter: (facetFilter: string) => void
  variantsPerHit: Record<string, number>
}

export const ProductSearchContext = createContext<ProductSearchContextType>({
  currentHits: 0,
  objectIDs: [],
  facetChips: [],
  facetConfig: [],
  facetDynamicConfig: {},
  guidedFilters: [],
  hasActiveFilter: false,
  hits: {},
  initialFacetStats: {},
  numericFilters: {},
  resetFilters: noop,
  setRangeFilter: noop,
  sort: noop,
  sorting: ProductSorting.Default,
  toggleFilter: noop,
  variantsPerHit: {},
})

export interface ProductSearchProviderProps {
  algoliaConfig?: BlockConfigAlgolia
  children?: React.ReactNode
  extraBlocks?: BlockTypeWithGlobal[]
  extraBlocksIDPrefix?: string
  facetConfig: ProductListBlockFragmentFragment['facetConfig']
  facets: string[]
  filters: string
  guidedFilters: PageProductListBlockGuidedFilter[] | null
  hitsPerPage: string
}

export const ProductSearchProvider: FC<ProductSearchProviderProps> = ({
  algoliaConfig,
  children,
  extraBlocks,
  extraBlocksIDPrefix,
  facetConfig,
  facets,
  filters,
  guidedFilters,
  hitsPerPage,
}) => {
  const {
    asPath,
    locale = '',
    pathname,
    query,
    replace: historyReplace,
  } = useRouter()
  const { store } = useContext(MetadataContext)
  const popularityGroup = useSplitTest('popularity_v1')

  const [options, setOptions] = useState<Options>({
    boostMainObjectIDs: algoliaConfig?.boostMainObjectIDs || [],
    boostSpecificPositions: algoliaConfig?.boostSpecificPositions || [],
    boostSubObjectIDs: algoliaConfig?.boostSubObjectIDs || [],
    buryObjectIDs: algoliaConfig?.buryObjectIDs || [],
    facets,
    facetFilters: algoliaConfig?.initialFacetFilters || [],
    filters,
    numericFilters: algoliaConfig?.initialNumericFilters || {},
    hitsPerPage,
    locale,
    optionalFilters: [],
    pagination: false,
    popularityGroup,
    sorting: algoliaConfig?.initialSorting || ProductSorting.Default,
  })

  const swr = useSWR(
    options,
    algoliaFetcher,
    {
      keepPreviousData: true,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  useEffect(() => {
    if (!algoliaConfig?.useQueryParams || typeof window === 'undefined') {
      return
    }

    // Remove all facet filters and add them back
    const nextQuery = omitBy(query, (_, queryKey) => startsWith(queryKey, 'filter_') || queryKey === 'sorting')

    forEach(
      groupBy(options.facetFilters, facetFilter => split(facetFilter, ':')[0]),
      (facetFilters, facetKey) => {
        const values = map(facetFilters, facetFilter => tail(split(facetFilter, ':', 2)))

        if (values.length === 0) {
          return
        }

        nextQuery[`filter_${snakeCase(replace(facetKey, /Facet$/, ''))}`] = join(sortBy(values), ',')
      },
    )

    forEach(options.numericFilters, ({
      max,
      min,
    }, facetKey) => {
      nextQuery[`filter_${snakeCase(replace(facetKey, /Facet$/, ''))}`] = `${min},${max}`
    })

    if (options.sorting !== ProductSorting.Default) {
      nextQuery.sorting = options.sorting
    }

    if (isEqual(query, nextQuery)) {
      return
    }

    historyReplace(
      {
        pathname,
        query: nextQuery,
      },
      {
        pathname: split(asPath, '?')[0],
        query: omit(nextQuery, ['slug']),
      },
      {
        locale,
        shallow: true,
        scroll: false,
      },
    )
  }, [
    algoliaConfig?.useQueryParams,
    asPath,
    historyReplace,
    locale,
    options.facetFilters,
    options.numericFilters,
    options.sorting,
    pathname,
    query,
  ])

  const toggleFilter = useCallback((facetFilter: string) => setOptions((state) => {
    if (!includes(state.facetFilters, facetFilter)) {
      try {
        const [facetFilterName, facetFilterValue] = facetFilter.split(':')
        ampli.plApplyFilter({
          filter_code: facetFilterName,
          filter_option_id: facetFilterValue,
        })
        gtagEvent('filter_changed', {
          filter_code: facetFilterName,
          filter_option_id: facetFilterValue,
        })
      } catch {
        // ignore errors
      }

      return {
        ...state,
        facetFilters: [
          ...state.facetFilters,
          facetFilter,
        ],
      }
    }

    return {
      ...state,
      facetFilters: without(state.facetFilters, facetFilter),
    }
  }), [setOptions])

  const resetFilters = useCallback(() => setOptions((state) => {
    if (size(state.facetFilters) === 0 && size(state.numericFilters) === 0) {
      return state
    }

    return {
      ...state,
      facetFilters: [],
      numericFilters: {},
    }
  }), [setOptions])

  const setRangeFilter = useCallback((key: string, min: number | undefined, max: number | undefined) => setOptions((state) => {
    const current = state.numericFilters[key]

    if (min === undefined || max === undefined) {
      if (!current) {
        return state
      }

      return {
        ...state,
        numericFilters: omit(state.numericFilters, [key]),
      }
    }

    if (current && current.min === min && current.max === max) {
      return state
    }

    try {
      ampli.plApplyFilter({
        filter_code: key,
        filter_option_id: `${min}-${max}`,
      })
      gtagEvent('filter_changed', {
        filter_code: key,
        filter_option_id: `${min}-${max}`,
      })
    } catch {
      // ignore errors
    }

    return {
      ...state,
      numericFilters: {
        ...state.numericFilters,
        [key]: {
          max,
          min,
        },
      },
    }
  }), [setOptions])

  const sort = useCallback((sorting: ProductSorting) => setOptions((state) => {
    if (state.sorting === sorting) {
      return state
    }

    try {
      ampli.plApplySorting({
        sorting_type: sorting,
      })

      gtagEvent('sorting_changed', {
        sorting_type: sorting,
      })
    } catch {
      // ignore errors
    }

    return {
      ...state,
      sorting,
    }
  }), [setOptions])

  const facetDynamicConfig = useMemo(
    () => reduce(facetConfig, (memo, facet) => { /* eslint-disable no-param-reassign */
      if (facet.type === ProductListBlockFacetType.EnumerableDefault
        || facet.type === ProductListBlockFacetType.EnumerableBoolean
        || facet.type === ProductListBlockFacetType.EnumerableColor
        || facet.type === ProductListBlockFacetType.EnumerableNumeric
      ) {
        let checkedCount = 0
        let groupCount = 0

        forEach(facet.values, (value) => {
          const key = `${facet.id}:${value.id}`
          const checked = options.facetFilters.includes(key)
          const count = swr?.data?.facets?.[facet.id!]?.[value.id!] || 0

          if (checked) {
            checkedCount += 1
          }

          memo[key] = {
            checked,
            count,
            key: value.key || '',
            name: value.name || null,
            type: 'value',
          }
        })

        forEach(facet.groups, (group) => {
          forEach(group.values, (value) => {
            const key = `${facet.id}:${value.id}`
            const checked = options.facetFilters.includes(key)
            const count = swr?.data?.facets?.[facet.id!]?.[value.id!] || 0

            if (checked) {
              checkedCount += 1
            }

            memo[key] = {
              checked,
              count,
              key: value.key || '',
              name: value.name || null,
              type: 'value',
            }
          })

          groupCount += 1
        })

        memo[facet.id!] = {
          count: checkedCount,
          name: facet.name || null,
          showGroupTitle: groupCount > 1 && facet.key !== 'colorShades',
          type: 'facet',
        }
      }

      if (facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) {
        const numericFilter = options.numericFilters?.[facet.id!]
        memo[facet.id!] = {
          count: numericFilter ? 1 : 0,
          name: facet.name || null,
          showGroupTitle: false,
          type: 'facet',
        }
      }

      return memo
    }, {} as Record<string, FacetDynamicConfig>),
    [
      facetConfig,
      options.facetFilters,
      options.numericFilters,
      swr?.data?.facets,
    ],
  )

  const facetChips = useMemo(
    () => {
      const currencyFraction = 10 ** (store.config?.currencyFraction || 2)
      const currencyUnit = getCurrencyUnit(store.id)

      const facetFilterChips = compact(map(options.facetFilters, (facetKey) => {
        const facetConfigValue = facetDynamicConfig[facetKey] as FacetDynamicConfigValue | undefined
        if (!facetConfigValue) {
          return null
        }

        return {
          id: facetKey,
          onClick: () => toggleFilter(facetKey),
          text: facetConfigValue.name,
        } as FacetChip
      }))

      const numericFilterChips = compact(map(options.numericFilters, (facet, facetKey) => {
        const facetConfigValue = facetDynamicConfig[facetKey] as FacetDynamicConfigValue | undefined
        if (!facetConfigValue) {
          return null
        }

        let defaults = '{{facetName}}: {{from}}-{{to}}'
        let from = facet.min || 0
        let to = facet.max || 0

        // TODO: detect based on type, currently isn't available in dynamic config
        if (facetKey.toLowerCase().includes('price')) {
          defaults = '{{facetName}}: {{from}}-{{to}} {{currencyUnit}}'
          from /= currencyFraction
          to /= currencyFraction
        }

        return {
          id: facetKey,
          onClick: () => setRangeFilter(facetKey, undefined, undefined),
          text: {
            defaults,
            values: {
              currencyUnit,
              facetName: facetConfigValue.name?.defaults || '',
              from,
              to,
            },
          },
        } as FacetChip
      }))

      return [
        ...facetFilterChips,
        ...numericFilterChips,
      ]
    },
    [
      facetDynamicConfig,
      options.facetFilters,
      options.numericFilters,
      setRangeFilter,
      store.config?.currencyFraction,
      store.id,
      toggleFilter,
    ],
  )

  // TODO: remove in favor of a number coming from the backend
  const variantsPerHit = useMemo(() => {
    if (!algoliaConfig?.hits) {
      return {}
    }

    const variantsPerGroup: Record<string, number> = {}

    forEach(algoliaConfig.hits, (hit) => {
      if (!hit.groupID) {
        return
      }

      variantsPerGroup[hit.groupID] = (variantsPerGroup[hit.groupID] || 0) + 1
    })

    const result: Record<string, number> = {}

    forEach(algoliaConfig.hits, (hit) => {
      if (!hit.groupID) {
        return
      }

      result[hit.id] = variantsPerGroup[hit.groupID] || 1
    })

    return result
  }, [algoliaConfig?.hits])

  const value = useMemo(
    (): ProductSearchContextType => ({
      currentHits: swr.data?.totalHits || 0,
      objectIDs: swr.data?.objectIDs ?? [],
      facetChips,
      facetConfig,
      facetDynamicConfig,
      guidedFilters,
      hasActiveFilter: size(options.facetFilters) !== 0 || size(options.numericFilters) !== 0,
      hits: algoliaConfig?.hits || {},
      initialFacetStats: algoliaConfig?.initialFacetStats || {},
      numericFilters: options.numericFilters,
      resetFilters,
      setRangeFilter,
      sort,
      sorting: options.sorting,
      toggleFilter,
      variantsPerHit,
    }),
    [
      algoliaConfig?.hits,
      algoliaConfig?.initialFacetStats,
      facetChips,
      facetConfig,
      facetDynamicConfig,
      guidedFilters,
      options.facetFilters,
      options.numericFilters,
      options.sorting,
      resetFilters,
      setRangeFilter,
      sort,
      swr.data?.objectIDs,
      swr.data?.totalHits,
      toggleFilter,
      variantsPerHit,
    ],
  )

  const extraBlocksComponents = useBlockComponents(!value.hasActiveFilter ? extraBlocks || [] : [], `${extraBlocksIDPrefix}-extra-block`)

  return (
    <ProductSearchContext.Provider value={value}>
      {children}
      {extraBlocksComponents}
    </ProductSearchContext.Provider>
  )
}
